// The map heatmap: one dense byte of intensity per cell, and ONE full-world
// quad that paints it (plan A.5 row 6, Part B §B.5).
//
// The public shape is `render/heatmap.ts`'s — `setMode`, `setFrame`,
// `setBlocked` — so a caller does not learn which client it is talking to,
// and `arcade/match.ts` could later port by an import swap (plan C.2).
//
// What changed underneath is the whole cost. The reference rebuilds twelve
// `Graphics` paths over about twenty thousand hexes whenever a field
// arrives. Here a field is one `texSubImage2D` of 20 KB, and the ramp — the
// bucket, the alpha and the dark-to-hue mix — is evaluated per pixel by the
// shader from uniforms the mode sets. Nothing is drawn while the mode is
// NONE: the layer is invisible, and an invisible layer skips both passes
// (ui-map-interface.md §10.3).
//
// The outline of the cell the heat cell peek reads (R1825) moved to the
// hover cell layer (`hovercell.ts`, R1875), which outlines the hovered
// cell under every peek of a map hover.

import type { Engine } from '../engine';
import type { DrawCtx, FrameInfo, Layer } from '../layer';
import type { WorldPass } from '../gfx/worldquad';
import { UniformValues } from '../gfx/uniforms';
import { HEAT_UNIFORMS, heatProgram } from '../shaders/heatmap';
import { rgb } from '../../render/color';
import { onKnobs, type Knobs } from '../knobs';
import { HeatmapKind } from '../../gen/proto/aquaphobia';
import { HEAT_ALPHA_HI, HEAT_ALPHA_LO, heatMode } from '../../state';

export class HeatmapLayer implements Layer {
  readonly name = 'heatmap';

  private readonly pass: WorldPass;
  private readonly u = new UniformValues(HEAT_UNIFORMS);
  private readonly stopKnobs: () => void;
  private kind: HeatmapKind = HeatmapKind.HEATMAP_KIND_NONE;
  private shown = false;
  /** The last field of the mode on, or null until one arrives. The round
   *  minimap reads it to paint the same field in its disc (R1801). */
  private cells: Uint8Array | null = null;

  constructor(private readonly engine: Engine) {
    this.pass = engine.newWorldPass(heatProgram());
    this.pass.setExtra((p) => this.u.apply(p));
    this.stopKnobs = onKnobs((k) => this.applyKnobs(k));
  }

  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /**
   * The terrain mask.
   *
   * The reference needs it to drop ridge cells while it buckets. The shader
   * reads the blocked plane itself, on the sampler the terrain layer
   * already fills, so nothing is copied here — the call stays because the
   * two layers must answer the same messages.
   */
  setBlocked(_blocked: Uint8Array | null): void { /* the shader reads uBlocked */ }

  /**
   * Switch modes.
   *
   * The field on screen belongs to the mode that asked for it, so it is
   * dropped: the map goes empty until the server answers the new
   * subscription, rather than showing kills under a sovereignty legend.
   */
  setMode(kind: HeatmapKind): void {
    if (kind === this.kind) return;
    this.kind = kind;
    this.engine.heat.fill(0);
    this.cells = null;
    // The client draws CURRENTS and DEPTH in their own layers (R2870), so
    // this layer stays empty for them.
    this.shown = kind !== HeatmapKind.HEATMAP_KIND_NONE && !heatMode(kind).local;
    if (this.shown) this.applyMode();
  }

  /**
   * One pushed field. A frame for a mode we are no longer on is ignored —
   * the server answers an unsubscribe with whatever was already in flight.
   */
  setFrame(kind: HeatmapKind, cells: Uint8Array): void {
    if (kind !== this.kind || kind === HeatmapKind.HEATMAP_KIND_NONE) return;
    this.engine.heat.set(cells);
    this.cells = cells;
  }

  /** The mode that paints, NONE while none does. */
  get mode(): HeatmapKind { return this.kind; }

  /** The field of that mode, or null until the server sent one (R1801). */
  get field(): Uint8Array | null { return this.cells; }

  update(_frame: FrameInfo): void { /* a field changes only when one arrives */ }

  draw(ctx: DrawCtx): void {
    this.pass.draw(ctx.cam, this.engine.env);
  }

  dispose(): void {
    this.stopKnobs();
    this.pass.dispose();
  }

  /** The mode's ramp, as the uniforms the shader mixes it from. */
  private applyMode(): void {
    const mode = heatMode(this.kind);
    this.u.set('uHeatColor', rgb(mode.color));
    this.u.set('uHeatDark', rgb(mode.dark ?? mode.color));
    this.u.set('uHeatHasDark', mode.dark === undefined ? 0 : 1);
    this.u.set('uHeatAlphaLo', mode.alphaLo ?? HEAT_ALPHA_LO);
    this.u.set('uHeatAlphaHi', mode.alphaHi ?? HEAT_ALPHA_HI);
  }

  private applyKnobs(k: Knobs): void {
    this.u.set('uHeatSmooth', k.heat === 'smooth' ? 1 : 0);
  }
}
